import { supabase } from "@/lib/supabase";

export const DEFAULT_STORAGE_THUMBNAIL = {
  width: 320,
  height: 180,
  quality: 55,
  resize: "cover" as const,
};

const DEFAULT_SIGNED_URL_TTL_SECONDS = 60 * 60;
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const DEBUG_STORAGE_IMAGES =
  import.meta.env.DEV &&
  (import.meta.env.VITE_DEBUG_IMAGE_URLS === "true" ||
    (typeof window !== "undefined" && window.localStorage?.getItem("debug:image-urls") === "true"));

type ResizeMode = "cover" | "contain" | "fill";

type CachedSignedUrl = {
  signedUrl: string;
  expiresAtMs: number;
};

export type StorageObjectRef = {
  bucket: string;
  path: string;
};

export type StorageImageTransformOptions = {
  width?: number;
  height?: number;
  quality?: number;
  resize?: ResizeMode;
};

export type SignedStorageImageOptions = StorageImageTransformOptions & {
  bucket?: string;
  expiresIn?: number;
  variant?: "full" | "thumb";
};

const signedUrlCache = new Map<string, CachedSignedUrl>();
const signedUrlInflight = new Map<string, Promise<string>>();
const debugLogDedup = new Map<string, number>();

function isExternalOrLocalAssetUrl(value: string): boolean {
  return (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:") ||
    value.startsWith("/")
  );
}

function decodePath(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizePath(value: string): string {
  return decodePath(value.split("?")[0] ?? "").replace(/^\/+/, "");
}

function getCacheKey(ref: StorageObjectRef, options: Required<Pick<SignedStorageImageOptions, "variant" | "expiresIn">> & StorageImageTransformOptions): string {
  const width = options.width ?? "full";
  const height = options.height ?? "full";
  const quality = options.quality ?? "default";
  const resize = options.resize ?? "none";
  return [ref.bucket, ref.path, options.variant, width, height, quality, resize, options.expiresIn].join("::");
}

function logImageUrlEvent(event: "hit" | "miss" | "inflight" | "signed", key: string, details: Record<string, unknown>) {
  if (!DEBUG_STORAGE_IMAGES) return;
  const now = Date.now();
  const dedupeKey = `${event}:${key}`;
  const last = debugLogDedup.get(dedupeKey) ?? 0;
  if (now - last < 5_000) return;
  debugLogDedup.set(dedupeKey, now);
  console.info(`[storage-image:${event}]`, details);
}

export function parseSupabaseStorageUrl(value: string): StorageObjectRef | null {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;

  const match = trimmed.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/?#]+)\/([^?#]+)/);
  if (!match) return null;

  const bucket = decodePath(match[1] ?? "");
  const path = normalizePath(match[2] ?? "");
  if (!bucket || !path) return null;
  return { bucket, path };
}

export function resolveStorageObjectRef(value: string, bucket?: string): StorageObjectRef | null {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;

  const fromUrl = parseSupabaseStorageUrl(trimmed);
  if (fromUrl) return fromUrl;

  if (!bucket || isExternalOrLocalAssetUrl(trimmed)) return null;
  const path = normalizePath(trimmed);
  return path ? { bucket, path } : null;
}

export function getStorageStableKey(value: string, fallback: string, bucket?: string): string {
  const ref = resolveStorageObjectRef(value, bucket);
  if (ref) return `${ref.bucket}/${ref.path}`;

  const trimmed = String(value || "").trim();
  return trimmed ? trimmed.split("?")[0] || fallback : fallback;
}

export async function createSignedStorageImageUrl(
  value: string,
  options: SignedStorageImageOptions = {},
): Promise<string> {
  const variant = options.variant ?? "full";
  const expiresIn = options.expiresIn ?? DEFAULT_SIGNED_URL_TTL_SECONDS;
  const ref = resolveStorageObjectRef(value, options.bucket);
  if (!ref) return value;

  const transform = variant === "thumb"
    ? {
        width: options.width ?? DEFAULT_STORAGE_THUMBNAIL.width,
        height: options.height ?? DEFAULT_STORAGE_THUMBNAIL.height,
        resize: options.resize ?? DEFAULT_STORAGE_THUMBNAIL.resize,
        quality: options.quality ?? DEFAULT_STORAGE_THUMBNAIL.quality,
      }
    : undefined;

  const cacheKey = getCacheKey(ref, { variant, expiresIn, ...transform });
  const cached = signedUrlCache.get(cacheKey);
  if (cached && cached.expiresAtMs - Date.now() > REFRESH_MARGIN_MS) {
    logImageUrlEvent("hit", cacheKey, { bucket: ref.bucket, path: ref.path, variant, transform });
    return cached.signedUrl;
  }
  if (cached) signedUrlCache.delete(cacheKey);

  const inflight = signedUrlInflight.get(cacheKey);
  if (inflight) {
    logImageUrlEvent("inflight", cacheKey, { bucket: ref.bucket, path: ref.path, variant, transform });
    return inflight;
  }

  logImageUrlEvent("miss", cacheKey, { bucket: ref.bucket, path: ref.path, variant, transform });
  const promise = supabase.storage
    .from(ref.bucket)
    .createSignedUrl(ref.path, expiresIn, transform ? { transform } : undefined)
    .then(({ data, error }) => {
      if (error) throw error;
      if (!data?.signedUrl) throw new Error("Failed to generate signed image URL");
      signedUrlCache.set(cacheKey, {
        signedUrl: data.signedUrl,
        expiresAtMs: Date.now() + expiresIn * 1000,
      });
      logImageUrlEvent("signed", cacheKey, { bucket: ref.bucket, path: ref.path, variant, transform });
      return data.signedUrl;
    })
    .finally(() => {
      signedUrlInflight.delete(cacheKey);
    });

  signedUrlInflight.set(cacheKey, promise);
  return promise;
}

export async function createSignedStorageThumbnailUrl(
  value: string,
  options: Omit<SignedStorageImageOptions, "variant"> = {},
): Promise<string> {
  return createSignedStorageImageUrl(value, { ...options, variant: "thumb" });
}

export async function createSignedStorageFullImageUrl(
  value: string,
  options: Omit<SignedStorageImageOptions, "variant" | "width" | "height" | "quality" | "resize"> = {},
): Promise<string> {
  return createSignedStorageImageUrl(value, { ...options, variant: "full" });
}
